import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface PaginationProps {
  page: number
  pageSize: number
  total: number
  onPage: (page: number) => void
}

/** The page numbers to show: first, last, and two around the current one; null = gap. */
function pageNumbers(page: number, pages: number): (number | null)[] {
  const wanted = new Set([1, pages, page - 1, page, page + 1].filter((n) => n >= 1 && n <= pages))
  const sorted = [...wanted].sort((a, b) => a - b)
  const out: (number | null)[] = []
  for (const number of sorted) {
    const previous = out[out.length - 1]
    if (typeof previous === 'number' && number - previous > 1) out.push(null)
    out.push(number)
  }
  return out
}

/**
 * "1–50 van 1.234" with Vorige / page numbers / Volgende (port of Percival's
 * Pagination). Below 768 px only Vorige and Volgende show; the shadcn `Button`
 * already makes them 44 px tall there.
 */
export function Pagination({ page, pageSize, total, onPage }: PaginationProps) {
  const pages = Math.max(1, Math.ceil(total / pageSize))
  // A page past the last one shows no rows: "0–0 van 312", never "351–312 van 312".
  const empty = total === 0 || page > pages
  const first = empty ? 0 : (page - 1) * pageSize + 1
  const last = empty ? 0 : Math.min(page * pageSize, total)
  return (
    <nav
      aria-label="Paginering"
      className="flex flex-wrap items-center justify-between gap-2 text-sm"
    >
      <span className="text-muted-foreground tabular-nums">
        {first.toLocaleString('nl-NL')}–{last.toLocaleString('nl-NL')} van{' '}
        {total.toLocaleString('nl-NL')}
      </span>
      <div className="flex flex-wrap items-center gap-1">
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
        >
          <ChevronLeft /> Vorige
        </Button>
        <span className="flex items-center gap-1 max-md:hidden">
          {pageNumbers(page, pages).map((number, index) =>
            number === null ? (
              <span key={`gap-${index}`} className="px-1 text-muted-foreground" aria-hidden>
                …
              </span>
            ) : (
              <Button
                key={number}
                type="button"
                size="sm"
                variant={number === page ? 'default' : 'ghost'}
                aria-current={number === page ? 'page' : undefined}
                aria-label={`Pagina ${number}`}
                onClick={() => onPage(number)}
                className={cn('min-w-8 tabular-nums', number === page && 'pointer-events-none')}
              >
                {number}
              </Button>
            ),
          )}
        </span>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={page >= pages}
          onClick={() => onPage(page + 1)}
        >
          Volgende <ChevronRight />
        </Button>
      </div>
    </nav>
  )
}
